import React, { Suspense, lazy } from "react";
import { Navigate, Outlet, Route, BrowserRouter, Routes } from "react-router-dom";
import { AuthProvider, canAccess, useAuth } from "./auth";
import { ToastProvider } from "./ui";
import { Shell } from "./layout/Shell";
import type { ModuleKey } from "./types";
import Login from "./pages/Login";

// Each module is its own chunk — Dashboard alone pulls in recharts, no
// reason for every other page's first load to pay for that.
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Reports = lazy(() => import("./pages/Reports"));
const SupportTickets = lazy(() => import("./pages/SupportTickets"));
const Notifications = lazy(() => import("./pages/Notifications"));
const ListingApprovals = lazy(() => import("./pages/ListingApprovals"));
const PostContent = lazy(() => import("./pages/PostContent"));
const UsersVerification = lazy(() => import("./pages/UsersVerification"));
const Admins = lazy(() => import("./pages/Admins"));

function FullScreenSpinner() {
  return (
    <div style={{ height: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <span className="spinner" />
    </div>
  );
}

/** Layout route: gates on auth+admin, then renders the sidebar shell around whichever module route matched. */
function AuthGate() {
  const { loading, admin } = useAuth();
  if (loading) return <FullScreenSpinner />;
  if (!admin) return <Navigate to="/login" replace />;
  return (
    <Shell>
      <Suspense fallback={<FullScreenSpinner />}>
        <Outlet />
      </Suspense>
    </Shell>
  );
}

/** /login itself has to check the opposite direction: a successful sign-in updates auth state, but the URL is still /login until something sends it elsewhere — nothing else does that, so this route does. */
function LoginRoute() {
  const { loading, admin } = useAuth();
  if (!loading && admin) return <Navigate to="/" replace />;
  return <Login />;
}

function RequireModule({ moduleKey, children }: { moduleKey: ModuleKey; children: React.ReactNode }) {
  const { admin } = useAuth();
  if (!canAccess(admin?.role ?? null, moduleKey)) {
    return (
      <div className="page">
        <div className="empty-state">
          <p className="t-title">Not permitted</p>
          <p className="t-small" style={{ marginTop: 6 }}>
            Your role doesn't include access to this module.
          </p>
        </div>
      </div>
    );
  }
  return <>{children}</>;
}

export default function App() {
  return (
    <AuthProvider>
      <ToastProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginRoute />} />
            <Route element={<AuthGate />}>
              <Route path="/" element={<RequireModule moduleKey="dashboard"><Dashboard /></RequireModule>} />
              <Route path="/reports" element={<RequireModule moduleKey="reports"><Reports /></RequireModule>} />
              <Route path="/tickets" element={<RequireModule moduleKey="tickets"><SupportTickets /></RequireModule>} />
              <Route path="/notifications" element={<RequireModule moduleKey="notifications"><Notifications /></RequireModule>} />
              <Route path="/listings" element={<RequireModule moduleKey="listingApprovals"><ListingApprovals /></RequireModule>} />
              <Route path="/post" element={<RequireModule moduleKey="postContent"><PostContent /></RequireModule>} />
              <Route path="/users"element={<RequireModule moduleKey="userVerification"><UsersVerification /></RequireModule>} />
              <Route path="/admins" element={<RequireModule moduleKey="admins"><Admins /></RequireModule>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </ToastProvider>
    </AuthProvider>
  );
}
